import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart3, CheckCircle2, Loader2, Vote } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useActivePlatformPoll,
  useVotePlatformPoll,
  type ActivePollOption,
} from "@/hooks/usePlatformPoll";
import { useIsAdmin } from "@/hooks/useUserRole";

export function ActivePollCard() {
  const { data: poll, isLoading } = useActivePlatformPoll();
  const { isAdmin } = useIsAdmin();
  const vote = useVotePlatformPoll();
  const [selected, setSelected] = useState<string | null>(null);

  const sortedOptions = useMemo(() => {
    if (!poll) return [] as ActivePollOption[];
    return [...poll.options].sort(
      (a, b) => a.order_index - b.order_index || a.option_text.localeCompare(b.option_text)
    );
  }, [poll]);

  if (isLoading || !poll) return null;

  const hasVoted = !!poll.user_voted_option_id;
  const showAdminResults = isAdmin;
  const totalVotes = poll.total_votes || 0;
  const locked = hasVoted && !poll.allow_change_vote;
  const votedOption = sortedOptions.find((o) => o.id === poll.user_voted_option_id);

  const submitVote = () => {
    if (!selected) return;
    vote.mutate(
      {
        pollId: poll.id,
        optionId: selected,
        existingVoteOptionId: poll.user_voted_option_id,
      },
      {
        onSuccess: () => setSelected(null),
      }
    );
  };

  const handleOptionClick = (optionId: string) => {
    if (locked || vote.isPending) return;
    setSelected(optionId);
  };

  // Mensagem de agradecimento (não-admin, já votou e não pode alterar)
  if (hasVoted && !isAdmin && !poll.allow_change_vote) {
    return (
      <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
        <CardContent className="p-5 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="gap-1.5">
              <Vote className="h-3 w-3" />
              Enquete ativa
            </Badge>
            <span className="text-xs text-muted-foreground">{poll.title}</span>
          </div>
          <div className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/10 p-4">
            <CheckCircle2 className="h-6 w-6 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
                Obrigado pela sua interação!
              </h3>
              <p className="text-sm text-muted-foreground">
                Estamos melhorando cada vez mais para você. Seu voto foi registrado com sucesso.
              </p>
              {votedOption && (
                <p className="text-sm mt-2">
                  Sua escolha: <span className="font-medium text-foreground">{votedOption.option_text}</span>
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background">
      <CardContent className="p-5 md:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Badge variant="secondary" className="gap-1.5 animate-pulse">
            <Vote className="h-3 w-3" />
            Enquete ativa
          </Badge>
          <span className="text-xs text-muted-foreground">{poll.title}</span>
        </div>

        <h3 className="text-lg md:text-xl font-semibold mb-4 text-foreground">
          {poll.question}
        </h3>

        {hasVoted && !isAdmin && votedOption && (
          <div className="mb-4 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 p-3">
            <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-foreground">Obrigado pela sua interação!</p>
              <p className="text-muted-foreground">
                Voto atual: <span className="font-medium text-foreground">{votedOption.option_text}</span> · você pode alterar enquanto a enquete estiver ativa.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-4">
          {sortedOptions.map((opt) => {
            const isUserChoice = poll.user_voted_option_id === opt.id;
            const isSelected = selected === opt.id;
            const pct =
              totalVotes > 0 ? Math.round((opt.votes_count / totalVotes) * 100) : 0;

            if (showAdminResults) {
              return (
                <div
                  key={opt.id}
                  className={cn(
                    "relative rounded-md border p-3 overflow-hidden",
                    isUserChoice
                      ? "border-primary bg-primary/5"
                      : "border-border bg-background"
                  )}
                >
                  <div
                    className={cn(
                      "absolute inset-y-0 left-0 transition-all",
                      isUserChoice ? "bg-primary/20" : "bg-muted"
                    )}
                    style={{ width: `${pct}%` }}
                  />
                  <div className="relative flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {isUserChoice && (
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                      )}
                      <span className="font-medium text-sm truncate">
                        {opt.option_text}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                      <span>{opt.votes_count} voto{opt.votes_count === 1 ? "" : "s"}</span>
                      <span className="font-semibold text-foreground">{pct}%</span>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleOptionClick(opt.id)}
                disabled={vote.isPending}
                className={cn(
                  "w-full text-left rounded-md border p-3 transition-colors flex items-center justify-between gap-2",
                  isUserChoice
                    ? "border-primary bg-primary/10"
                    : isSelected
                    ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                    : "border-border hover:bg-muted/50",
                  vote.isPending && "cursor-wait opacity-70"
                )}
              >
                <span className="text-sm font-medium">{opt.option_text}</span>
                <div className="flex items-center gap-2">
                  {isUserChoice && (
                    <Badge variant="secondary" className="gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Seu voto
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <BarChart3 className="h-3 w-3" />
            {isAdmin ? (
              <>
                {totalVotes} voto{totalVotes === 1 ? "" : "s"} no total
              </>
            ) : hasVoted ? (
              <>Selecione uma nova opção e clique em alterar voto</>
            ) : (
              <>Selecione uma opção e clique em salvar · sua resposta é anônima</>
            )}
          </p>

          {!isAdmin && !hasVoted && (
            <Button
              size="sm"
              onClick={submitVote}
              disabled={!selected || vote.isPending}
            >
              {vote.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Salvar voto"
              )}
            </Button>
          )}

          {!isAdmin && hasVoted && poll.allow_change_vote && (
            <Button
              size="sm"
              onClick={submitVote}
              disabled={!selected || selected === poll.user_voted_option_id || vote.isPending}
            >
              {vote.isPending ? "Salvando..." : "Alterar voto"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
